package com.heelion.farmafreelance.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import farmafreelance.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import com.heelion.farmafreelance.models.*
import com.heelion.farmafreelance.utils.TimeUtils
import com.heelion.farmafreelance.voice.VoiceIntent
import kotlinx.datetime.Instant
import androidx.compose.foundation.background
import androidx.compose.ui.draw.blur
import androidx.compose.ui.draw.clip
import androidx.compose.foundation.border
import androidx.compose.material.icons.automirrored.filled.List

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun FarmaciaScreen(
    viewModel: AuthViewModel,
    pendingIntent: VoiceIntent? = null,
    onPendingIntentConsumed: () -> Unit = {},
    onVoiceIntentRouted: (VoiceIntent) -> Unit = {}
) {
    val subStatus = viewModel.subscriptionStatus
    var query by remember { mutableStateOf("") }
    val searchRegioni = remember { mutableStateListOf<String>() }
    val searchProvince = remember { mutableStateListOf<String>() }
    val searchComuni = remember { mutableStateListOf<String>() }
    var searchCap by remember { mutableStateOf("") }
    var startDate by remember { mutableStateOf<Long?>(null) }
    var endDate by remember { mutableStateOf<Long?>(null) }
    
    var showRegioneMenu by remember { mutableStateOf(false) }
    var showProvinciaMenu by remember { mutableStateOf(false) }
    var showComuneMenu by remember { mutableStateOf(false) }
    var showDatePicker by remember { mutableStateOf(false) }

    val listRegioni = viewModel.listaRegioni
    val listProvince = viewModel.listaProvince
    val listComuni = viewModel.listaComuni

    var farmacieFiltrate by remember { mutableStateOf(listOf<Farmacia>()) }
    var isLoading by remember { mutableStateOf(false) }
    var selectedFarmacia by remember { mutableStateOf<Farmacia?>(null) }
    var viewModeMappa by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val uriHandler = LocalUriHandler.current

    fun apriMaps(f: Farmacia) {
        val indirizzoCompleto = listOfNotNull(f.indirizzo, f.citta).joinToString(" ")
        val encodedAddress = indirizzoCompleto.replace(" ", "+")
        uriHandler.openUri("https://www.google.com/maps/search/?api=1&query=$encodedAddress")
    }

    // Carica le regioni all'avvio se la lista è vuota
    LaunchedEffect(Unit) {
        if (viewModel.listaRegioni.isEmpty()) {
            viewModel.loadRegioni()
        }
    }

    fun performSearch() {
        scope.launch {
            isLoading = true
            try {
                farmacieFiltrate = viewModel.searchFarmacie(
                    query = query,
                    regione = searchRegioni.joinToString(","),
                    provincia = searchProvince.joinToString(","),
                    comune = searchComuni.joinToString(","),
                    cap = searchCap,
                    startDate = startDate,
                    endDate = endDate
                )
            } catch (e: Exception) {
                e.printStackTrace()
            } finally {
                isLoading = false
            }
        }
    }

    // Corpo del comando vocale/testuale estratto in una funzione a sé: così sia il pulsante
    // microfono di questa schermata sia un intento già riconosciuto altrove (es. dalla Home,
    // instradato qui tramite [pendingIntent]) eseguono esattamente la stessa logica.
    fun gestisciIntentVocale(text: String, intent: VoiceIntent): String {
        return when (intent) {
            is VoiceIntent.CercaFarmacie -> {
                // Ogni ricerca a voce riparte da zero: non deve sommarsi a criteri
                // già impostati (dal profilo del farmacista, dai filtri manuali, o
                // da una ricerca vocale precedente), altrimenti si rischia di
                // combinare criteri incompatibili (es. regione preimpostata "Lazio"
                // + comune appena detto "Savona" = nessun risultato).
                searchRegioni.clear()
                searchProvince.clear()
                searchComuni.clear()
                query = ""
                intent.regione?.let { searchRegioni.add(it) }
                intent.provincia?.let { searchProvince.add(it) }
                intent.comune?.let { searchComuni.add(it) }
                intent.query?.let { query = it }
                performSearch()
                "Ho sentito: \"$text\". Ho cercato in base a quello che ho capito."
            }
            is VoiceIntent.ApriMappa -> {
                val target = farmacieFiltrate.firstOrNull()
                if (target != null) {
                    apriMaps(target)
                    "Apro Google Maps per \"${target.nomeFarmacia}\"."
                } else {
                    "Nessun risultato su cui aprire la mappa: cerca prima una farmacia."
                }
            }
            is VoiceIntent.MostraTurniFarmacia -> {
                val nome = intent.nomeFarmacia
                scope.launch {
                    val risultati = if (nome != null) viewModel.searchFarmacie(query = nome) else farmacieFiltrate
                    val trovata = risultati.firstOrNull()
                    if (trovata != null) {
                        if (nome != null) farmacieFiltrate = risultati
                        selectedFarmacia = trovata
                    }
                }
                if (nome != null) "Cerco i turni di \"$nome\"..." else "Apro i turni della farmacia trovata..."
            }
            is VoiceIntent.Unrecognized -> "Ho sentito: \"$text\". Non l'ho capito, riprova o usa i filtri qui sotto."
            // Non di competenza di questa schermata (creare/gestire un turno, cerca farmacisti,
            // filtro turni Home, navigazione ad altra sezione...): lo rimbalzo al router centrale.
            else -> {
                onVoiceIntentRouted(intent)
                "Ho sentito: \"$text\". Ti porto alla sezione giusta..."
            }
        }
    }

    LaunchedEffect(pendingIntent) {
        val i = pendingIntent
        if (i != null) {
            gestisciIntentVocale("(dalla Home)", i)
            onPendingIntentConsumed()
        }
    }

    // Carica preferenze dal profilo del professionista (come base iniziale)
    LaunchedEffect(viewModel.currentUserProfessionista) {
        val f = viewModel.currentUserProfessionista
        if (f != null) {
            if (f.regioneOperativita != null && !searchRegioni.contains(f.regioneOperativita)) {
                searchRegioni.add(f.regioneOperativita!!)
            }
            // Evitiamo duplicati durante la sincronizzazione
            f.provinceOperativita.forEach { if(!searchProvince.contains(it)) searchProvince.add(it) }
            f.comuniOperativita.forEach { if(!searchComuni.contains(it)) searchComuni.add(it) }
            
            if (searchRegioni.isNotEmpty()) {
                viewModel.updateProvincePerRegioni(searchRegioni.toList())
            }
            if (searchProvince.isNotEmpty()) {
                viewModel.updateComuniPerProvince(searchProvince.toList())
            }
            performSearch()
        }
    }

    val themeColorHex = viewModel.selectedThemeColor.hex.removePrefix("0x").toLong(16)
    val themeColor = Color(themeColorHex)
    val allLabel = stringResource(Res.string.all_filter)

    Column(modifier = Modifier.fillMaxSize()) {
        val days = subStatus.daysRemaining
        if (subStatus.isTrial && days != null) {
            TrialBanner(days)
        }
        
        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            item {
                // --- SEZIONE CRUSCOTTO ---
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(
                        containerColor = lerp(Color.White, themeColor, 0.15f)
                    ),
                    shape = RoundedCornerShape(16.dp),
                    border = androidx.compose.foundation.BorderStroke(
                        width = 0.5.dp,
                        color = if (themeColor == Color.White) Color.LightGray else themeColor.copy(alpha = 0.2f)
                    ),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                ) {
                    Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                        Box(
                            modifier = Modifier
                                .width(6.dp)
                                .fillMaxHeight()
                                .background(if (themeColor == Color.White) Color.Gray else themeColor)
                        )
                        Column(modifier = Modifier.padding(16.dp)) {
                            Text(
                                text = stringResource(Res.string.tab_search),
                                style = MaterialTheme.typography.headlineMedium,
                                fontWeight = FontWeight.Bold,
                                color = if (themeColor == Color.White) Color.Black else themeColor
                            )
                            Text(
                                text = stringResource(Res.string.search_pharmacies_subtitle),
                                style = MaterialTheme.typography.bodySmall,
                                color = Color.Gray
                            )

                            Spacer(modifier = Modifier.height(12.dp))
                            
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(Color.White.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
                                    .padding(4.dp),
                                horizontalArrangement = Arrangement.Center
                            ) {
                                FilterChip(
                                    selected = !viewModeMappa,
                                    onClick = { viewModeMappa = false },
                                    label = { Text("ELENCO") },
                                    leadingIcon = { Icon(Icons.AutoMirrored.Filled.List, null, modifier = Modifier.size(16.dp)) },
                                    shape = RoundedCornerShape(8.dp)
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                FilterChip(
                                    selected = viewModeMappa,
                                    onClick = { viewModeMappa = true },
                                    label = { Text("MAPPA") },
                                    leadingIcon = { Icon(Icons.Default.LocationOn, null, modifier = Modifier.size(16.dp)) },
                                    shape = RoundedCornerShape(8.dp)
                                )
                            }
                        }
                    }
                }
            }

            item {
                VoiceSearchButton(
                    hint = "Cerca a voce (es. \"cerca farmacie a Brescia\", \"in provincia di Bergamo\", \"in Lombardia\", \"che turni ha la farmacia Rossi\", \"apri maps\")",
                    onIntent = { text, intent -> gestisciIntentVocale(text, intent) },
                    onError = {}
                )
            }

            item {
                OutlinedTextField(
                    value = query,
                    onValueChange = { query = it; performSearch() },
                    label = { Text(stringResource(Res.string.pharmacy_name_vat_label)) },
                    modifier = Modifier.fillMaxWidth(),
                    leadingIcon = { Icon(Icons.Default.Search, contentDescription = null) },
                    shape = RoundedCornerShape(12.dp),
                    singleLine = true
                )
            }

            item {
                // REGIONI MULTIPLE
                ExposedDropdownMenuBox(
                    expanded = showRegioneMenu,
                    onExpandedChange = { showRegioneMenu = !showRegioneMenu },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    OutlinedTextField(
                        value = if (searchRegioni.isEmpty()) allLabel else "${searchRegioni.size} Regioni",
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(stringResource(Res.string.region_label_short)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = showRegioneMenu) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = showRegioneMenu, onDismissRequest = { showRegioneMenu = false }) {
                        DropdownMenuItem(text = { Text(allLabel) }, onClick = {
                            searchRegioni.clear()
                            searchProvince.clear()
                            searchComuni.clear()
                            viewModel.updateProvincePerRegioni(emptyList())
                            showRegioneMenu = false
                            performSearch()
                        })
                        listRegioni.forEach { r ->
                            val isSelected = searchRegioni.contains(r)
                            DropdownMenuItem(
                                text = {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Checkbox(checked = isSelected, onCheckedChange = null)
                                        Spacer(Modifier.width(8.dp))
                                        Text(r)
                                    }
                                },
                                onClick = { 
                                    if (isSelected) {
                                        searchRegioni.remove(r)
                                        val validProvs = searchProvince.filter { p -> viewModel.mappingProvinciaRegione[p] != r }
                                        searchProvince.clear()
                                        searchProvince.addAll(validProvs)
                                        viewModel.updateProvincePerRegioni(searchRegioni.toList())
                                    } else {
                                        searchRegioni.add(r)
                                        viewModel.updateProvincePerRegioni(searchRegioni.toList())
                                    }
                                    performSearch()
                                }
                            )
                        }
                    }
                }

                if (searchRegioni.isNotEmpty()) {
                    FlowRow(modifier = Modifier.fillMaxWidth().padding(top = 4.dp), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        searchRegioni.forEach { r ->
                            InputChip(
                                selected = true,
                                onClick = { 
                                    searchRegioni.remove(r)
                                    val validProvs = searchProvince.filter { p -> viewModel.mappingProvinciaRegione[p] != r }
                                    searchProvince.clear()
                                    searchProvince.addAll(validProvs)
                                    viewModel.updateProvincePerRegioni(searchRegioni.toList())
                                    performSearch()
                                },
                                label = { Text(r, fontSize = 10.sp) },
                                trailingIcon = { Icon(Icons.Default.Close, null, modifier = Modifier.size(12.dp)) }
                            )
                        }
                    }
                }
            }

            item {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    // PROVINCE MULTIPLE
                    ExposedDropdownMenuBox(
                        expanded = showProvinciaMenu,
                        onExpandedChange = { if(searchRegioni.isNotEmpty()) showProvinciaMenu = !showProvinciaMenu },
                        modifier = Modifier.weight(1f)
                    ) {
                        OutlinedTextField(
                            value = if (searchProvince.isEmpty()) allLabel else "${searchProvince.size} Prov.",
                            onValueChange = {},
                            readOnly = true,
                            enabled = searchRegioni.isNotEmpty(),
                            label = { Text(stringResource(Res.string.province_label_short)) },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = showProvinciaMenu) },
                            modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )
                        ExposedDropdownMenu(expanded = showProvinciaMenu, onDismissRequest = { showProvinciaMenu = false }) {
                            DropdownMenuItem(text = { Text(allLabel) }, onClick = {
                                searchProvince.clear()
                                searchComuni.clear()
                                viewModel.updateComuniPerProvince(emptyList())
                                showProvinciaMenu = false
                                performSearch()
                            })
                            listProvince.forEach { p ->
                                val isSelected = searchProvince.contains(p)
                                DropdownMenuItem(
                                    text = {
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Checkbox(checked = isSelected, onCheckedChange = null)
                                            Spacer(Modifier.width(8.dp))
                                            Text(p)
                                        }
                                    },
                                    onClick = { 
                                        if (isSelected) {
                                            searchProvince.remove(p)
                                            val validComs = searchComuni.filter { c -> viewModel.mappingComuneProvincia[c] != p }
                                            searchComuni.clear()
                                            searchComuni.addAll(validComs)
                                        } else {
                                            searchProvince.add(p)
                                        }
                                        viewModel.updateComuniPerProvince(searchProvince.toList())
                                        performSearch()
                                    }
                                )
                            }
                        }
                    }

                    // COMUNI MULTIPLI
                    ExposedDropdownMenuBox(
                        expanded = showComuneMenu,
                        onExpandedChange = { if(searchProvince.isNotEmpty()) showComuneMenu = !showComuneMenu },
                        modifier = Modifier.weight(1f)
                    ) {
                        OutlinedTextField(
                            value = if (searchComuni.isEmpty()) allLabel else "${searchComuni.size} Comuni",
                            onValueChange = {},
                            readOnly = true,
                            enabled = searchProvince.isNotEmpty(),
                            label = { Text(stringResource(Res.string.city_label_short), fontSize = 11.sp) },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = showComuneMenu) },
                            modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )
                        ExposedDropdownMenu(expanded = showComuneMenu, onDismissRequest = { showComuneMenu = false }) {
                            DropdownMenuItem(text = { Text(allLabel) }, onClick = {
                                searchComuni.clear()
                                showComuneMenu = false
                                performSearch()
                            })
                            listComuni.forEach { c ->
                                val isSelected = searchComuni.contains(c)
                                DropdownMenuItem(
                                    text = {
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Checkbox(checked = isSelected, onCheckedChange = null)
                                            Spacer(Modifier.width(8.dp))
                                            Text(c)
                                        }
                                    },
                                    onClick = { 
                                        if (isSelected) searchComuni.remove(c) else searchComuni.add(c)
                                        performSearch()
                                    }
                                )
                            }
                        }
                    }
                }
                
                if (searchProvince.isNotEmpty() || searchComuni.isNotEmpty()) {
                    FlowRow(modifier = Modifier.fillMaxWidth().padding(top = 4.dp), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        searchProvince.forEach { p ->
                            InputChip(
                                selected = true, 
                                onClick = { 
                                    searchProvince.remove(p)
                                    val validComs = searchComuni.filter { c -> viewModel.mappingComuneProvincia[c] != p }
                                    searchComuni.clear()
                                    searchComuni.addAll(validComs)
                                    viewModel.updateComuniPerProvince(searchProvince.toList())
                                    performSearch() 
                                }, 
                                label = { Text(p, fontSize = 9.sp) }
                            )
                        }
                        searchComuni.take(5).forEach { c ->
                            InputChip(selected = true, onClick = { searchComuni.remove(c); performSearch() }, label = { Text(c, fontSize = 9.sp) })
                        }
                        if (searchComuni.size > 5) {
                            Text("+${searchComuni.size - 5}", fontSize = 10.sp)
                        }
                    }
                }
            }

            item {
                // Altri filtri (CAP, Date, ecc.)
                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    OutlinedTextField(
                        value = searchCap,
                        onValueChange = { searchCap = it; performSearch() },
                        label = { Text(stringResource(Res.string.cap_label_short), fontSize = 11.sp) },
                        modifier = Modifier.weight(0.3f),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true
                    )
                    Spacer(Modifier.width(8.dp))
                    
                    val dateText = if (startDate != null && endDate != null) {
                        val s = Instant.fromEpochMilliseconds(startDate!!).toLocalDateTime(TimeZone.UTC).date
                        val e = Instant.fromEpochMilliseconds(endDate!!).toLocalDateTime(TimeZone.UTC).date
                        "${s.dayOfMonth}/${s.monthNumber} - ${e.dayOfMonth}/${e.monthNumber}"
                    } else stringResource(Res.string.any_period)

                    Button(
                        onClick = { showDatePicker = true },
                        modifier = Modifier.weight(0.7f),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = if (startDate != null) themeColor else Color.Gray)
                    ) {
                        Icon(Icons.Default.DateRange, null, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(4.dp))
                        Text(dateText.uppercase(), fontSize = 10.sp, fontWeight = FontWeight.Bold)
                    }
                }
            }

            item {
                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }

            if (farmacieFiltrate.isEmpty() && !isLoading) {
                item { Text(stringResource(Res.string.no_pharmacies_found), modifier = Modifier.padding(16.dp), color = Color.Gray) }
            }

                    val shown = if (viewModel.accessoCompleto) farmacieFiltrate else farmacieFiltrate.take(3)
                    items(shown) { f ->
                        FarmaciaResultItem(f, isBlurred = false, viewModel = viewModel, onOpenMaps = { apriMaps(f) }) { selectedFarmacia = f }
                    }
                    if (!viewModel.accessoCompleto && farmacieFiltrate.size > 3) {
                        items(farmacieFiltrate.drop(3)) { f ->
                            FarmaciaResultItem(f, isBlurred = true, viewModel = viewModel, onOpenMaps = {}) {}
                        }
                    }
                    
                    item {
                        Spacer(modifier = Modifier.height(100.dp))
                    }
                }
            }

    if (selectedFarmacia != null) {
        Dialog(onDismissRequest = { selectedFarmacia = null }) {
            ProfileDetailScreen(farmacia = selectedFarmacia, viewModel = viewModel, onBack = { selectedFarmacia = null })
        }
    }
}

@Composable
fun FarmaciaResultItem(f: Farmacia, isBlurred: Boolean, viewModel: AuthViewModel, onOpenMaps: () -> Unit, onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth().clickable { if (!isBlurred) onClick() },
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(2.dp)
    ) {
        Box {
            ListItem(
                modifier = if (isBlurred) Modifier.blur(8.dp) else Modifier,
                headlineContent = { Text(f.nomeFarmacia, fontWeight = FontWeight.Bold) },
                supportingContent = { Text(f.indirizzo) },
                leadingContent = {
                    Surface(color = MaterialTheme.colorScheme.primaryContainer, shape = RoundedCornerShape(8.dp), modifier = Modifier.size(40.dp)) {
                        Box(contentAlignment = Alignment.Center) { Icon(AppIcons.Store, null, tint = MaterialTheme.colorScheme.primary) }
                    }
                },
                trailingContent = if (isBlurred) null else {
                    {
                        IconButton(onClick = onOpenMaps) {
                            Icon(Icons.Default.Place, contentDescription = "Apri in Google Maps", tint = MaterialTheme.colorScheme.primary)
                        }
                    }
                }
            )
            if (isBlurred) {
                Box(modifier = Modifier.matchParentSize().background(Color.Black.copy(alpha = 0.3f)), contentAlignment = Alignment.Center) {
                    PremiumGuard(viewModel) {}
                }
            }
        }
    }
}
